import classNames from 'classnames/bind';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { PostAction } from 'types/post';
import { PostListUser } from 'types/user';
import { rawId } from 'utils/rawId';
import { goToSpaces } from 'utils/routerHelpers';

import React, { ReactNode } from 'react';

import { Link } from '@thinkific/chalk/Link';
import { Text } from '@thinkific/chalk/Text';
import { PushPinSimple } from '@thinkific/chalk/react-icons';

import { useCommunityContext } from 'components/CommunityContext';
import UserDetail from 'components/UserDetail';
import PostActions from 'components/new/Posts/PostActions';

import styles from '../Post.module.scss';

const cx = classNames.bind(styles);

interface PostHeaderProps {
  isFollowing: boolean;
  author?: PostListUser | null;
  editedAt: string;
  creationDateTime: string;
  pinnedAt: string;
  postActionsList: PostAction[];
  showFollowingTag: boolean;
  title: string | ReactNode;
  showSpace: boolean;
  spaceId: string;
  spaceName: string;
  isReplyPanel: boolean;
}

const PostHeader = ({
  isFollowing,
  author,
  editedAt,
  creationDateTime,
  pinnedAt,
  showFollowingTag,
  postActionsList,
  title,
  showSpace,
  spaceId,
  spaceName,
  isReplyPanel,
}: PostHeaderProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { community, trackEvent } = useCommunityContext();

  const handleClickOnSpaceBadge = (event, filter, name) => {
    event.preventDefault();
    event.stopPropagation();

    trackEvent(TrackingEvent.SPACE_CLICKED, {
      [TrackingProperty.SPACE_ID]: rawId(filter),
      [TrackingProperty.SPACE_NAME]: name,
    });

    goToSpaces(router, filter, community.id, true);
  };

  return (
    <>
      {showSpace && (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          className={cx('post__detail-header__space-name')}
          emphasis="low"
          variant="body.xs"
          onClick={(event) => handleClickOnSpaceBadge(event, spaceId, spaceName)}
        >
          {`${t('components-postHeader-posted_in', 'Posted in')} ${spaceName}`}
        </Link>
      )}
      <div
        className={cx('post__detail-header', {
          'is-following': showFollowingTag && isFollowing,
        })}
      >
        <UserDetail
          author={author}
          editedAt={editedAt}
          isFollowing={showFollowingTag && isFollowing}
          isReplyPanel={isReplyPanel}
          time={creationDateTime || ''}
        />
        <div className={cx('post__detail-actions')}>
          {pinnedAt && (
            <span className={cx('post__detail-actions__pinned')}>
              <PushPinSimple color="onLight.text.tertiary" data-qa="icon-pin" size="xs" filled />
            </span>
          )}
          {postActionsList.length > 0 && <PostActions actionsList={postActionsList} t={t} />}
        </div>
      </div>
      {title && (
        <Text
          as="span"
          className={cx('post__title')}
          dataQa="main-post-title__text"
          variant="body.mdEmphasis"
        >
          {title}
        </Text>
      )}
    </>
  );
};

export default PostHeader;
